import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, ArrowLeft, ArrowUpRight, Loader2 } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import { BUSINESS, INSTAGRAM_POSTS } from "../data";

const PAGE_SIZE = 12;

export default function InstagramPage() {
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // load Instagram embed.js + reprocess on visibility change
  useEffect(() => {
    const ensureScript = () =>
      new Promise((resolve) => {
        if (window.instgrm) return resolve();
        const existing = document.querySelector(
          'script[src="https://www.instagram.com/embed.js"]'
        );
        if (existing) {
          existing.addEventListener("load", () => resolve());
          return;
        }
        const s = document.createElement("script");
        s.src = "https://www.instagram.com/embed.js";
        s.async = true;
        s.onload = () => resolve();
        document.body.appendChild(s);
      });

    ensureScript().then(() => {
      if (window.instgrm && window.instgrm.Embeds) {
        window.instgrm.Embeds.process();
      }
    });
  }, [visible]);

  const items = INSTAGRAM_POSTS.slice(0, visible);
  const hasMore = visible < INSTAGRAM_POSTS.length;

  return (
    <div data-testid="instagram-page" className="bg-[#0A0C10] text-white min-h-screen">
      <Nav />

      {/* Page header */}
      <header className="relative pt-32 sm:pt-40 pb-16 border-b border-white/5 overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -top-20 right-0 h-[420px] w-[420px] rounded-full bg-[#00F0FF]/10 blur-[160px]"
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <Link
            to="/"
            data-testid="back-to-home"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#00F0FF] transition-colors mb-8"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <div className="text-[11px] uppercase tracking-[0.24em] text-[#00F0FF] mb-5">
                / Instagram Archive
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.02]">
                Every recent build from{" "}
                <span className="text-gradient-cyan">{BUSINESS.instagramHandle}</span>
              </h1>
              <p className="mt-6 text-[#9CA3AF] text-base leading-relaxed max-w-xl">
                The full archive of recent jobs — coding, retrofits, ambient
                lighting, security and more. Tap any post to view it on
                Instagram.
              </p>
            </div>
            <a
              href={BUSINESS.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="ig-page-follow"
              className="inline-flex items-center gap-2 bg-[#00F0FF] hover:bg-[#33F3FF] text-[#0A0C10] font-semibold text-sm px-5 py-3 rounded-sm transition-colors self-start hover:shadow-[0_0_30px_rgba(0,240,255,0.25)]"
            >
              <Instagram className="h-4 w-4" />
              Follow @autotech.uk
            </a>
          </div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-white/5 pt-8">
            <Stat label="Total Posts" value={INSTAGRAM_POSTS.length} />
            <Stat label="Showing" value={items.length} />
            <Stat label="Location" value={BUSINESS.location} />
            <Stat label="Service" value="Mobile" />
          </div>
        </div>
      </header>

      {/* Posts grid */}
      <main className="relative py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6"
            data-testid="instagram-archive-grid"
          >
            {items.map((p) => (
              <PostEmbed key={p.shortcode} type={p.type} shortcode={p.shortcode} />
            ))}
          </div>

          {hasMore && (
            <div className="mt-12 flex justify-center">
              <button
                data-testid="load-more-archive"
                onClick={() =>
                  setVisible((v) => Math.min(v + PAGE_SIZE, INSTAGRAM_POSTS.length))
                }
                className="inline-flex items-center gap-2 border border-white/15 text-white hover:bg-white/5 px-6 py-3 rounded-sm text-sm font-medium transition-colors"
              >
                Load more posts
                <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Bottom CTA */}
          <div className="mt-20 border-t border-white/5 pt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <div className="font-heading text-2xl text-white font-medium">
                Like what you see?
              </div>
              <p className="mt-2 text-sm text-[#9CA3AF] max-w-md">
                Get a quote on your build — usually back to you the same day.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href={BUSINESS.whatsappText}
                target="_blank"
                rel="noreferrer"
                data-testid="ig-page-whatsapp"
                className="inline-flex items-center gap-2 bg-[#00F0FF] hover:bg-[#33F3FF] text-[#0A0C10] font-semibold text-sm px-5 py-3 rounded-sm transition-colors"
              >
                Message on WhatsApp
              </a>
              <Link
                to="/#services"
                className="inline-flex items-center gap-2 border border-white/15 text-white hover:bg-white/5 px-5 py-3 rounded-sm text-sm font-medium transition-colors"
              >
                Browse services
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-heading text-2xl sm:text-3xl text-white tracking-tight">
        {value}
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-white/50">
        {label}
      </div>
    </div>
  );
}

function PostEmbed({ type, shortcode }) {
  const url = `https://www.instagram.com/${type}/${shortcode}/`;
  return (
    <div
      data-testid={`ig-archive-post-${shortcode}`}
      className="relative bg-[#12141C] border border-white/5 rounded-sm overflow-hidden"
    >
      <div className="absolute inset-0 grid place-items-center text-white/30 pointer-events-none">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
      <blockquote
        className="instagram-media relative z-10"
        data-instgrm-permalink={url}
        data-instgrm-version="14"
        data-instgrm-captioned="true"
        style={{
          background: "#FFF",
          border: 0,
          borderRadius: 2,
          margin: 0,
          maxWidth: "100%",
          minWidth: "auto",
          padding: 0,
          width: "100%",
        }}
      >
        <a href={url} target="_blank" rel="noreferrer">
          View post on Instagram
        </a>
      </blockquote>
    </div>
  );
}
